import { FC, ReactNode, useState } from 'react';
import { AlertCircle, Check, Clock, Loader2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Modal } from '@/components/modal';
import { useWorkflowPlacement } from '@/hooks/queries/use-workflow-placement';
import { useSubmitDecision } from '@/hooks/mutations/use-submit-decision';
import { ApprovalSide, SideDecision } from '@/services/workflow/qualification';
import { cn } from '@/lib/utils';

const SIDE_LABELS: Record<ApprovalSide, string> = {
  capitol: 'Capitol',
  client: 'Client',
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

const SideRow: FC<{ side: SideDecision }> = ({ side }) => {
  const label = SIDE_LABELS[side.side] ?? side.side;
  if (!side.decision) {
    return (
      <li className='flex items-start gap-2 text-sm'>
        <Clock className='size-4 shrink-0 mt-0.5 text-muted-foreground' />
        <span>{label} sign-off outstanding</span>
      </li>
    );
  }
  const rejected = side.decision === 'rejected';
  return (
    <li className='flex items-start gap-2 text-sm'>
      {rejected ? (
        <X className='size-4 shrink-0 mt-0.5 text-red-600' />
      ) : (
        <Check className='size-4 shrink-0 mt-0.5 text-emerald-600' />
      )}
      <span>
        <span className={cn(rejected && 'text-red-700 dark:text-red-400')}>
          {label} {rejected ? 'rejected' : 'approved'}
        </span>
        {side.approver && (
          <span className='text-muted-foreground'> by {side.approver}</span>
        )}
        {side.decided_at && (
          <span className='text-muted-foreground'>
            {' '}
            on {formatDate(side.decided_at)}
          </span>
        )}
        {side.feedback && (
          <span className='block text-xs text-muted-foreground mt-0.5'>
            {side.feedback}
          </span>
        )}
      </span>
    </li>
  );
};

interface SignOffModalProps {
  isOpen: boolean;
  onClose: () => void;
  orgId: string | undefined;
  workflowId: string;
  workflowName: string;
}

export const SignOffModal: FC<SignOffModalProps> = ({
  isOpen,
  onClose,
  orgId,
  workflowId,
  workflowName,
}) => {
  const [feedback, setFeedback] = useState('');

  const {
    data: placement,
    isLoading,
    error,
  } = useWorkflowPlacement(orgId, isOpen ? workflowId : undefined);

  const review = placement?.review;
  const decision = useSubmitDecision({
    orgId,
    workflowId,
    attemptId: review?.attempt_id,
    onSettled: onClose,
  });

  const viewerSide = review?.viewer_side ?? null;
  const isSubmitting = decision.isPending;

  let content: ReactNode = null;

  if (isLoading) {
    content = (
      <div className='flex items-center gap-2 py-6 text-sm text-muted-foreground'>
        <Loader2 className='size-4 animate-spin' />
        Loading sign-off status...
      </div>
    );
  } else if (error) {
    content = (
      <div className='flex items-start gap-2 py-4 text-sm text-red-700 dark:text-red-400'>
        <AlertCircle className='size-4 shrink-0 mt-0.5' />
        <span>{error.message}</span>
      </div>
    );
  } else if (review) {
    content = (
      <div className='space-y-4 py-2'>
        <p className='text-sm'>
          In <strong>pre-UAT</strong> for {review.wave.name}, entered{' '}
          {formatDate(placement.entered_at)}.
        </p>

        <ul className='space-y-1.5'>
          {review.sides.map((side) => (
            <SideRow key={side.side} side={side} />
          ))}
        </ul>

        {review.awaiting_settlement && (
          <div className='flex items-start gap-2 rounded-md bg-muted p-3 text-sm'>
            <Clock className='size-4 shrink-0 mt-0.5 text-muted-foreground' />
            <span>
              Every required sign-off is in. An admin promotes it into UAT from
              the UAT pipeline board.
            </span>
          </div>
        )}

        {review.incumbent && (
          <div className='flex items-start gap-2 rounded-md bg-muted p-3 text-sm'>
            <AlertCircle className='size-4 shrink-0 mt-0.5 text-amber-500' />
            <span>
              Approving replaces the copy already live in UAT
              {review.incumbent.from_wave
                ? ` from ${review.incumbent.from_wave}`
                : ''}
              . The previous version is retained for audit.
            </span>
          </div>
        )}

        {viewerSide ? (
          <div className='space-y-1.5'>
            <label className='text-sm font-medium'>
              Feedback (required when rejecting)
            </label>
            <Textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder='What did you find?'
              rows={3}
            />
            <p className='text-xs text-muted-foreground'>
              Signing as {SIDE_LABELS[viewerSide]}.
            </p>
          </div>
        ) : (
          <p className='text-xs text-muted-foreground'>
            You are not on the approver list for this client and region, so this
            is read-only.
          </p>
        )}
      </div>
    );
  }

  let footer: ReactNode = <Button onClick={onClose}>Close</Button>;
  if (viewerSide) {
    footer = (
      <>
        <Button variant='outline' onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          variant='outline'
          disabled={isSubmitting || !feedback.trim()}
          onClick={() =>
            decision.mutate({
              decision: 'rejected',
              side: viewerSide,
              feedback: feedback.trim(),
            })
          }
        >
          Reject
        </Button>
        <Button
          className='gap-2'
          disabled={isSubmitting}
          onClick={() =>
            decision.mutate({
              decision: 'approved',
              side: viewerSide,
              feedback: feedback.trim() || undefined,
            })
          }
        >
          {isSubmitting && <Loader2 className='size-4 animate-spin' />}
          Approve
        </Button>
      </>
    );
  }

  return (
    <Modal
      title='Sign-off status'
      description={workflowName}
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        // Not dismissable mid-decision, for the same reason the apply
        // modal is not: this is a write, and the in-flight `onSuccess`
        // would otherwise land against whichever row the reused
        // list-table instance has open by then.
        if (!open && !isSubmitting) onClose();
      }}
      footer={footer}
      contentClassName='sm:max-w-lg'
    >
      {content}
    </Modal>
  );
};
